import {
  BACKGROUND_MANAGEMENT_ROW_LABEL,
  DURASPEED_EXCLUDED_APP_LABELS,
  DURASPEED_EXCLUSION_STEP_NAME,
  PERFORMANCE_ACTIVITY,
  PERFORMANCE_PACKAGE,
} from '@mxa-argos/modules/forja/constants/forja.constants';
import type { ForjaStep } from '@mxa-argos/modules/forja/interfaces/forja-step.interface';
import type { ForjaUISwitch } from '@mxa-argos/modules/forja/interfaces/forja-ui-switch.interface';
import { ForjaError } from '@mxa-argos/modules/forja/lib/forja-error';
import {
  readLabels,
  searchLabeledSwitch,
  UIAutomator,
} from '@mxa-argos/modules/forja/lib/provisioning/ui-automator';
import { sleep } from '@mxa-argos/modules/forja/lib/sleep';

const FOREGROUND_TIMEOUT_MS = 8_000;
const FOREGROUND_POLL_MS = 300;
const TAP_SETTLE_MS = 500;
const TAP_CONFIRM_TIMEOUT_MS = 5_000;
const SCROLL_PASSES = 8;

// The DuraSpeed list is an exclusion list: a switch that is ON exempts that app from background
// management. Requires MOS installed, since the rows do not exist before.
export function duraspeedExclusionStep(): ForjaStep {
  let wasConfirmed = false;

  return {
    name: DURASPEED_EXCLUSION_STEP_NAME,
    verify: async () => wasConfirmed,
    apply: async (device) => {
      const automator = new UIAutomator(device);
      await openDuraspeedScreen(automator);
      await enableExclusions(automator);
      await device.shell('input', 'keyevent', 'KEYCODE_HOME');
      wasConfirmed = true;
    },
    verifyTimeoutMs: 10_000,
  };
}

async function enableExclusions(automator: UIAutomator): Promise<void> {
  const pending = new Set(DURASPEED_EXCLUDED_APP_LABELS);
  const seenLabels = new Set<string>();

  await automator.scrollToTop();

  for (let pass = 0; pass < SCROLL_PASSES && pending.size > 0; pass += 1) {
    const xml = await automator.dumpUI();
    readLabels(xml).forEach((label) => seenLabels.add(label));

    for (const label of [...pending]) {
      const labelSwitch = searchLabeledSwitch(xml, label);

      if (!labelSwitch) {
        continue;
      }

      if (!labelSwitch.isChecked) {
        const wasSwitchedOn = await tapSwitchOn(automator, labelSwitch, label);

        if (!wasSwitchedOn) {
          throw new ForjaError(
            'UI_AUTOMATION_FAILED',
            `'${label}' DuraSpeed switch is still off ${TAP_CONFIRM_TIMEOUT_MS}ms after tapping it at (${labelSwitch.centerX}, ${labelSwitch.centerY})`,
          );
        }
      }

      pending.delete(label);
    }

    if (pending.size > 0) {
      await automator.scrollDown();
    }
  }

  if (pending.size > 0) {
    throw new ForjaError(
      'UI_AUTOMATION_FAILED',
      `rows not found in the DuraSpeed app list: ${[...pending].join(', ')}. Rows seen: ${[...seenLabels].join(' | ') || '<empty dump>'}`,
    );
  }
}

async function tapSwitchOn(
  automator: UIAutomator,
  labelSwitch: ForjaUISwitch,
  label: string,
): Promise<boolean> {
  const deadline = Date.now() + TAP_CONFIRM_TIMEOUT_MS;

  await automator.tapSwitch(labelSwitch);

  while (Date.now() < deadline) {
    await sleep(TAP_SETTLE_MS);

    const xml = await automator.dumpUI().catch(() => null);

    if (xml && searchLabeledSwitch(xml, label)?.isChecked) {
      return true;
    }
  }

  return false;
}

async function openDuraspeedScreen(automator: UIAutomator): Promise<void> {
  const { device } = automator;

  await device.shell('input', 'keyevent', 'KEYCODE_WAKEUP');
  await device.shell('wm', 'dismiss-keyguard');
  // An open notification shade covers the screen the taps below aim at, and it is what the UI
  // dump would return.
  await device.collapseNotificationShade();
  // `am start` resumes a live task at its last screen, so without this a retry stays inside the
  // app list and never finds the row tapped below.
  await device.shell('am', 'force-stop', PERFORMANCE_PACKAGE);

  const output = await device.shell('am', 'start', '-n', PERFORMANCE_ACTIVITY);

  if (!(await waitForForeground(automator))) {
    // Lock task only allows the packages the DPC whitelisted, and the Performance screen is a
    // package of its own (com.ape.performances): allowing Settings does not cover it.
    const kioskHint = (await device.isKioskEngaged())
      ? ` Kiosk mode is engaged, so ${PERFORMANCE_PACKAGE} must be whitelisted in the Proget policy to be reachable.`
      : '';

    throw new ForjaError(
      'UI_AUTOMATION_FAILED',
      `the Performance screen did not open.${kioskHint} am start said: ${output.replace(/\s+/g, ' ').trim()}. Foreground: ${await device.readForegroundWindow()}`,
    );
  }

  await automator.tapNodeByText(BACKGROUND_MANAGEMENT_ROW_LABEL);
  await automator.waitForSwitches();
}

async function waitForForeground(automator: UIAutomator): Promise<boolean> {
  const deadline = Date.now() + FOREGROUND_TIMEOUT_MS;

  while (Date.now() < deadline) {
    if (await automator.device.isAppInForeground(PERFORMANCE_PACKAGE)) {
      return true;
    }
    await sleep(FOREGROUND_POLL_MS);
  }

  return false;
}
